import { adminFetch, API_BASE, authHeaders } from "./_client";
import type { BrandRequest } from "./types";


export function fetchBrandRequests(
  token: string,
  opts: { status?: "pending" | "approved" | "rejected" | "all"; limit?: number; sort?: "oldest" | "newest" } = {},
) {
  const sp = new URLSearchParams();
  if (opts.status) sp.set("status", opts.status);
  if (opts.limit) sp.set("limit", String(opts.limit));
  if (opts.sort) sp.set("sort", opts.sort);
  const qs = sp.toString();
  return adminFetch<{ requests: BrandRequest[]; stale_after_hours: number }>(
    `/admin/brand-requests${qs ? `?${qs}` : ""}`,
    token,
  );
}


export function decideBrandRequest(
  token: string,
  id: number,
  body: { decision: "approve" | "reject"; canonical_name?: string; decision_note?: string },
) {
  return adminFetch<{
    success: true;
    decision: "approved" | "rejected";
    request_id: number;
    approved_make_id?: number;
    canonical_name?: string;
  }>(
    `/admin/brand-requests/${id}/decision`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}


// ── New 3-action resolution model (PUT handler) ────────────────────────────
// Replaces the binary approve/reject model with:
//   correct_approve — upsert/reactivate car_makes row, re-link cars, notify host
//   map_existing    — map to a verified car_makes row by make_id, re-link cars, notify host
//   discard         — internal cleanup; only when no car is linked
export function decideBrandRequestV2(
  token: string,
  id: number,
  body:
    | { action: "correct_approve"; canonical_name: string; decision_note?: string }
    | { action: "map_existing"; make_id: number; decision_note?: string }
    | { action: "discard"; decision_note?: string },
) {
  return adminFetch<{
    success: true;
    action: string;
    request_id: number;
    approved_make_id?: number;
    canonical_name?: string;
    linked_cars: number;
    auto_activated: number;
  }>(
    `/admin/brand-requests/${id}/decision`,
    token,
    { method: "PUT", body: JSON.stringify(body) },
  );
}


// ── Inline make registration ───────────────────────────────────────────────
// Registers a new verified make directly from the Correct Make panel.
// Returns `{ make_id, canonical_name }` on success, or the same shape with
// `conflict: true` when a row with the same normalised name already exists —
// the caller should fall back to "select this existing match" in that case.
export async function adminRegisterMake(
  token: string,
  body: { canonical_name: string },
): Promise<{ make_id: number; canonical_name: string; conflict?: true }> {
  const res = await fetch(`${API_BASE}/admin/makes`, {
    method: "POST",
    headers: authHeaders(token) as Record<string, string>,
    body: JSON.stringify(body),
  });
  // Parse JSON once; safe — the server always returns JSON.
  const json = await res.json().catch(() => ({})) as { make_id?: number; canonical_name?: string; error?: string };
  if (res.status === 409) {
    // Already registered — return the conflict row so the UI can suggest it.
    return { make_id: json.make_id as number, canonical_name: json.canonical_name as string, conflict: true };
  }
  if (!res.ok) {
    throw new Error(json?.error ?? `API error ${res.status}`);
  }
  return { make_id: json.make_id as number, canonical_name: json.canonical_name as string };
}


// ── Admin make correction ──────────────────────────────────────────────────
// The only UI path to fix a legacy typo'd make on any car. Accepts a
// verified make_id (no free-text). Clears any stale pending_make_request_id.
export function adminSetListingMake(
  token: string,
  carId: number,
  body: { make_id: number },
) {
  return adminFetch<{
    success: true;
    car_id: number;
    make_id: number;
    canonical_name: string;
  }>(
    `/admin/listings/${carId}/make`,
    token,
    { method: "PUT", body: JSON.stringify(body) },
  );
}
